import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { Card, Table, TableBody, TableContainer } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack, useMediaQuery } from '@mui/system';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions, PaginatedResponse, SortConfig } from 'src/services/api.types';
import { Contact } from 'src/services/contacts/contacts.types';
import { ListEntityType } from 'src/services/lists/lists.types';

import ContactsTableToolbar from 'src/components/custom-tables/contacts-table/contacts-table-toolbar';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import { TenantType } from 'src/types/enums';

import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { openMultiMailerDialog } from 'src/store/slices/app/appSlice';
import { toggleCandidateEditorSidesheet } from 'src/store/slices/candidate/candidateSlice';
import { useAppDispatch } from 'src/store/store';
import { SortOrder } from 'src/services/common.types';
import BulkContactUploadModal from 'src/sections/contacts/modal/bulk-contact-import-modal';
import { ContactsTableRow } from './contacts-table-row';

type Props = {
  contacts?: PaginatedResponse<Contact>;
  pageOptions: PageOptions;
  isLoading: boolean;
  onChangePage: (_event: any, pageNumber: number) => void;
  onChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFilters?: (type: string, value: string) => void;
  onSortChange?: (sortConfig: { sort_by: string; sort_order: SortOrder }) => void;
  clearFilters?: () => void;
  onAddContact?: () => void;
  onRemoveContact?: (contact: Contact) => void;
  onSendEmail?: (contact: Contact) => void;
};

export default function ContactsTable({
  contacts,
  pageOptions,
  isLoading = false,
  onChangePage,
  onChangeRowsPerPage,
  onFilters,
  onSortChange,
  clearFilters,
  onAddContact,
  onRemoveContact,
  onSendEmail
}: Props) {

  const table = useTable();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tenantType = useOrgTenant();

  const { t } = useTranslate();

  const addToListControl = useBoolean();
  const bulkImportControl = useBoolean();

  const denseHeight = table.dense ? 56 : 56 + 20;

  const notFound = !contacts?.count;

  const isCandidate = tenantType === TenantType.Candidate;

  const tableLabels = useMemo(
    () => [
      { id: 'name', label: t('contacts.table.name') },
      { id: 'email', label: t('contacts.table.email') },
      { id: 'phone', label: t('contacts.table.phone') },
      { id: 'source', label: t('contacts.table.source') },
      { id: 'owner', label: t('contacts.table.owner') },
      { id: 'notes', label: '' }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCandidate]
  );

  const handleAddToList = useCallback(async (companyIds: string[]) => {
    addToListControl.onTrue();
  }, [addToListControl]);

  const onSuccessAddToList = useCallback(() => {
    table.onSelectAllRows(false);
    addToListControl.onFalse();
  }, [addToListControl, table]);

  const handleSendMultiEmail = useCallback(() => {
    if (table.selected.length === 0) return;

    dispatch(openMultiMailerDialog({
      targetType: MailerTargetType.CONTACT,
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      entities: table.selected,
    }));

  }, [dispatch, table]);

  const handleCreateCandidate = useCallback((contact: Contact) => {
    dispatch(toggleCandidateEditorSidesheet({
      status: true,
      candidateId: null,
      firstName: contact.first_name,
      lastName: contact.last_name,
      email: contact.email,
    }));
  }, [dispatch]);

  return (
    <Card sx={{ borderRadius: 0 }}>
      {
        isLoading ? (
          <TableSkeleton />
        )
          :
          <>
            {
              (onFilters && clearFilters) && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <ContactsTableToolbar
                    table={table}
                    onAddContact={onAddContact}
                    onFilters={onFilters}
                    clearFilters={clearFilters}
                    onAddToList={!isCandidate ? handleAddToList : undefined}
                    onSendMultiEmail={handleSendMultiEmail}
                    onSortChange={onSortChange}
                    onBulkImport={bulkImportControl.onTrue}
                  />
                </Stack>
              )
            }

            <TableContainer sx={{ overflow: 'unset' }}>
              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'}>
                  {
                    !isMobile && (
                      <TableHeadCustom
                        headLabel={tableLabels}
                        rowCount={contacts?.results.length ?? 0}
                        numSelected={table.selected.length}
                        onSelectAllRows={!isCandidate ? (checked) =>
                          table.onSelectAllRows(
                            checked,
                            (contacts?.results ?? []).map((row) => row.id) as string[]
                          ) : undefined
                        }
                      />
                    )
                  }

                  <TableBody>
                    {contacts?.results?.map((row) =>
                      <ContactsTableRow
                        key={row.id} row={row}
                        onRemoveContact={onRemoveContact}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={!isCandidate ? () => table.onSelectRow(row.id) : undefined}
                        onSendEmail={onSendEmail}
                        onCreateCandidate={handleCreateCandidate}
                      />
                    )}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, contacts?.count ?? 0)}
                    />

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={contacts?.count ?? 0}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </>
      }

      <AddItemsModal
        open={addToListControl.value}
        onClose={addToListControl.onFalse}
        onSuccess={onSuccessAddToList}
        items={table.selected}
        entityType={ListEntityType.CONTACT}
      />

      <BulkContactUploadModal open={bulkImportControl.value} onClose={bulkImportControl.onFalse} />
    </Card>
  );
}
